import { useRef } from "react";
import { Download, Printer, RefreshCw } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { saveAs } from "file-saver";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilenceError } from "@/core/utils/func/notifApp";
import { useAgentQrMe } from "@/features/wallet/api/use-agent-qr-me";

const QR_DISPLAY_SIZE = 240;
const QR_FG = "#2D3B50";
const QR_BG = "#ffffff";

/**
 * Carte QR agent (guide v2.4 §6.3, `GET /WalletQrCodes/agent/me`).
 *
 * QR **statique** et imprimable (préfixe jeton `A1.`, AES-256-GCM, non
 * tournant) : un agent est un point de vente fixe. Le client le scanne pour un
 * dépôt espèces (`AGENT_CASH_IN`), dont le float de l'agent est la contrepartie.
 * Rôles : `flashpay-agents` (accès à l'endpoint).
 */
export function AgentQrCard() {
  const { t } = useTypedTranslation();
  const tx = (key: string) => t(key as never, {} as never) as string;
  const { data, isFetching, error, refetch } = useAgentQrMe();
  const canvasRef = useRef<HTMLDivElement>(null);

  const download = () => {
    if (!data?.payloadToken) return;
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (blob) saveAs(blob, `qr-agent-${data.agentId ?? "me"}.png`);
    }, "image/png");
  };

  const print = () => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const w = window.open("", "_blank", "noopener,noreferrer,width=600,height=800");
    if (!w) {
      notifSilenceError(tx("wallet.agentQr.printBlocked"));
      return;
    }
    w.document.write(
      `<html><head><title>QR agent</title></head><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;"><img src="${dataUrl}" style="width:60%;max-width:420px;" onload="window.print();" /></body></html>`
    );
    w.document.close();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span>{tx("wallet.agentQr.title")}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching ? (
              <ButtonSpinner />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-3">
        <p className="text-sm text-muted-foreground max-w-md text-center">
          {tx("wallet.agentQr.description")}
        </p>
        {error ? (
          <p className="text-sm text-destructive">{error.message}</p>
        ) : null}
        {data?.payloadToken ? (
          <>
            <div ref={canvasRef} className="rounded-md bg-white p-3">
              <QRCodeCanvas
                value={data.payloadToken}
                size={QR_DISPLAY_SIZE}
                includeMargin={false}
                bgColor={QR_BG}
                fgColor={QR_FG}
                level="H"
              />
            </div>
            <NoTranslate as="span" className="font-mono text-xs text-muted-foreground">
              {data.agentId}
            </NoTranslate>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={download}>
                <Download className="mr-2 h-4 w-4" />
                {tx("wallet.agentQr.download")}
              </Button>
              <Button size="sm" onClick={print}>
                <Printer className="mr-2 h-4 w-4" />
                {tx("wallet.agentQr.print")}
              </Button>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
